import {
  SearchOptionType,
  type SearchOptions,
  type SearchRequest,
} from 'gpcore-grpc/gpcore/type/v1/search_pb';
import { type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

export default (
  searchData: Record<string, string | number | boolean | null>,
  searchOptions: Ref<SearchOptions>,
  paramPrefix: Ref<string>,
  searchRequest: Ref<SearchRequest>,
) => {
  const route = useRoute();
  const router = useRouter();

  const clearRoute = () => {
    const query = { ...route.query };

    // filter query beginning with paramPrefix
    Object.keys(query).forEach((key) => {
      if (key.startsWith(paramPrefix.value)) {
        delete query[key];
      }
    });

    router.push({ query });
  };

  const mapSearchDataToRouteQuery = () => {
    // copy current query
    const query = { ...route.query };

    searchOptions.value.options.forEach((searchOption) => {
      // first look for the search options in the search request
      const searchItem = searchRequest.value.filters.find(
        (filter) => filter.name === searchOption.name,
      );

      const filterKey = `${paramPrefix.value}.${searchOption.name}`;

      // if the search option is not in the search request, delete it from the query
      if (searchItem == null) {
        delete query[filterKey];
        return;
      }

      switch (searchOption.type) {
        case SearchOptionType.DROPDOWN:
          query[filterKey] = String(searchItem.value.value);
          break;

        case SearchOptionType.BOOLEAN:
          query[filterKey] = String(searchItem.value.value);
          break;

        case SearchOptionType.STRING:
          query[filterKey] = String(searchItem.value.value);
          break;

        default:
          break;
      }
    });

    return query;
  };

  const updateRoute = () => {
    clearRoute();

    const query = mapSearchDataToRouteQuery();

    query[`${paramPrefix.value}.extended`] = 'true';
    router.push({ query });
  };

  const mapRouteQueryToSearchData = () => {
    // reset all search data
    searchOptions.value.options.forEach((option) => {
      searchData[option.name] = null;
    });

    if (route.query[`${paramPrefix.value}.extended`] === 'true') {
      Object.entries(route.query).forEach(([key, value]) => {
        if (key.startsWith(paramPrefix.value)) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const [_, name] = key.split('.');
          if (name === 'extended') {
            return;
          }

          // we have to map boolean value from query to boolean
          if (
            searchOptions.value.options.find((o) => o.name === name)?.type ===
            SearchOptionType.BOOLEAN
          ) {
            // for the case that the query value is an empty string, we have to set the value to null
            if (value === '') {
              searchData[name] = null;
              return;
            }
            // for the case that the query value is a string, we have to map it to boolean
            searchData[name] = value === 'true';
            return;
          }

          // for the case that the query value is a string we do not care
          searchData[name] = value as string;
        }
      });
    }
  };

  return {
    mapSearchDataToRouteQuery,
    mapRouteQueryToSearchData,
    clearRoute,
    updateRoute,
  };
};
